import _ from 'lodash';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';

import {
  CategoryDesktopLayoutTemplate,
  CategoryMobileLayoutTemplate
} from 'components/atomic/template';
import { DEVICE_SURFACE } from 'config/common';
import { GetUiDeviceSurface } from 'helpers/screenResolutionHelper';
import {
  getCategoryPriceRange,
  getCategorySEODetails,
  getCategoryTree
} from 'services/categories.service';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';
import { getProducts } from 'services/product.service';

const publicBuildCategoryListPageCount =
  process.env.NEXT_PUBLIC_BUILD_L0_CATEGORY_LISTING_PAGE_COUNT;

const ParentCategory = ({
  categories,
  category,
  categorySeoDetails,
  initialProducts,
  parentCategories,
  parentCategoryName,
  parentSlug,
  price,
  productsCount
}) => {
  const router = useRouter();
  const surface = GetUiDeviceSurface();

  const breadcrumbsInfo = [
    {
      link: getPageURL({
        pageName: ROUTE_CONFIG.HOME_PAGE.label
      }),
      name: 'Home'
    },
    { link: '', name: parentCategoryName }
  ];

  if (router.isFallback) {
    return <div id='product-list'>Loading...</div>;
  }
  const categoryLayoutMap = {
    [DEVICE_SURFACE.MOBILE]: CategoryMobileLayoutTemplate,
    [DEVICE_SURFACE.WEB]: CategoryDesktopLayoutTemplate
  };
  const CategoryLayout = categoryLayoutMap[surface];

  return (
    <CategoryLayout
      {...{
        breadcrumbsInfo,
        categories,
        category,
        categorySeoDetails,
        initialProducts,
        parentCategories,
        parentCategoryName,
        parentSlug,
        priceRange: price,
        productsCount
      }}
    />
  );
};

export default ParentCategory;

export const getStaticProps = async ({ locale = 'en', params }) => {
  const { parentSlug } = params;

  const { entity: { rows: levelZeroCategories = [] } = {} } =
    await getCategoryTree();

  const matchedLevelZeroCategories = levelZeroCategories.find(
    ({ slug }) => slug === parentSlug
  );

  if (_.isEmpty(matchedLevelZeroCategories)) {
    return {
      notFound: true
    };
  }

  const category = matchedLevelZeroCategories;

  const { entity: categorySeoDetails = {} } = await getCategorySEODetails(
    parentSlug
  );

  const {
    entity: { count: productsCount = 0, rows: initialProducts = [] } = {}
  } = await getProducts({
    limit: 15,
    parentCategory: parentSlug,
    skip: 0
  });

  const {
    entity: {
      aggregations: { price }
    }
  } = await getCategoryPriceRange({
    key: 'assignedToCategories',
    value: parentSlug
  });

  const footer = await serverSideTranslations(locale, ['footer']);

  return {
    props: {
      categories: matchedLevelZeroCategories?.childCategories || [],
      category,
      categorySeoDetails,
      initialProducts,
      parentCategories: levelZeroCategories,
      parentCategoryName: matchedLevelZeroCategories?.name,
      parentSlug,
      price,
      productsCount,
      ...footer
    },
    revalidate: 300
  };
};

export const getStaticPaths = async () => {
  const { entity: { rows: levelZeroCategories = [] } = {} } =
    await getCategoryTree();

  const categoryList = levelZeroCategories.map(({ slug: parentSlug }) => ({
    params: {
      parentSlug
    }
  }));

  const buildCategories =
    publicBuildCategoryListPageCount > 0
      ? categoryList.slice(0, publicBuildCategoryListPageCount)
      : categoryList;

  return {
    fallback: true,
    paths: buildCategories
  };
};
